import styled from '@emotion/styled'
import { Checkbox } from 'antd'
import { OutlineButton } from '../styles'

export {
  ActionCard,
  FieldGroup,
  FieldLabel,
  OutlineButton,
  PrimaryButton
} from '../styles'

export const LoadingRoot = styled.div`
  display: flex;
  justify-content: center;
  padding: 64px 0;
`

export const FormWidth = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 740px;
  margin: 0 auto;
  width: 100%;
`

export const SidePanel = styled.div`
  background-color: #ffffff;
  border-radius: 6px;
  padding: 24px;
  height: fit-content;
  max-width: 317px;

  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const TableWrapper = styled.div`
  border: 1px solid #d2d2d9;
  border-radius: 4px;
  overflow: hidden;
`

export const TableHeaderBar = styled.div`
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 8px 16px;
  gap: 16px;
  background: #f3f2f4;
  border-bottom: 1px solid #d2d2d9;
  font-size: 13px;
  font-weight: 500;
  color: #77778e;
`

export const StoreRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid #ebebef;

  &:last-of-type {
    border-bottom: none;
  }
`

export const StoreColumn = styled.div`
  flex: 1;
  min-width: 0;
`

// The header tone is darker because it sits on TableHeaderBar's #f3f2f4
// rather than white.
export const ColumnDivider = styled.div<{ $header?: boolean }>(
  ({ $header }) => `
  align-self: stretch;
  width: 1px;
  background: ${$header ? '#e7e5e9' : '#f3f2f4'};
`
)

export const ShopColumn = styled.div`
  flex: 1;
  min-width: 0;
`

export const StoreName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #4a4a68;
`

export const StoreSubtext = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #77778e;
`

export const Bold = styled.strong`
  font-weight: 700;
`

// Spans the table it sits under, unlike OutlineButton (../styles), which sizes
// to its label.
export const LinkStoresButton = styled(OutlineButton)`
  width: 100%;
  margin-top: 12px;
`

// Centers the input against the full (possibly wrapped) label height instead
// of antd's first-line default.
export const CenteredCheckbox = styled(Checkbox, {
  // Emotion only auto-filters `$`-prefixed props for styled.tag; wrapping a
  // third-party component needs this, or the prop leaks onto the DOM node.
  shouldForwardProp: (prop) => !prop.startsWith('$')
})<{
  $inputMarginBottom: number
  $highlighted?: boolean
}>(
  ({ $inputMarginBottom, $highlighted }) => `
  display: flex;
  align-items: center;
  font-size: 12px;

  .ant-checkbox {
    margin-bottom: ${$inputMarginBottom}px;
  }

  ${
    $highlighted
      ? `
  // The hover selector too, or antd's own hover rule takes the border back.
  .ant-checkbox-inner,
  .ant-checkbox:hover .ant-checkbox-inner {
    border-color: #ff4d4f;
    box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2);
  }`
      : ''
  }
`
)

export const ConsentCard = styled.div`
  background-color: #fafafb;
  border: 1px solid #d2d2d9;
  border-radius: 4px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

// Overrides the Section Card's 24px gap for just this pair.
export const LoyaltyAndTableGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const ConsentText = styled.div`
  font-size: 13px;
  font-weight: 400;
  color: #4a4a68;

  strong {
    font-weight: 600;
  }
`

// Unlike ActionCard (../styles), which is right-aligned only, this leaves room
// for a left-side status line.
export const SummaryActionCard = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 1;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`

// SummaryActionCard's space-between expects two children, so the optional Back
// button is grouped with the primary action rather than added beside it.
export const SummaryActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

export const SummaryText = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #4a4a68;

  strong {
    font-weight: 600;
  }
`

export const PushOrdersBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #ffffff;
  border: 1px solid #d2d2d9;
  border-radius: 4px;
  padding: 8px 12px;
`

export const PushOrdersTitle = styled.p`
  font-size: 12px;
  font-weight: 500;
  color: #4a4a68;
  margin: 0;
`

export const PushOrdersDescription = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: #4a4a68;
  margin: 0;

  strong {
    font-weight: 700;
  }
`

export const ToggleColumn = styled.div`
  width: 72px;
  flex-shrink: 0;
  text-align: center;
`

export const OpenLeatButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 8px 16px;
  border: 1px solid #4a4a68;
  border-radius: 4px;
  color: #4a4a68;
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  &:hover,
  &:focus {
    color: #4a4a68;
    border-color: #4a4a68;
    text-decoration: none;
  }
`

export const FieldHint = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #77778e;
`

export const SidePanelTitle = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: #4a4a68;
  margin: 0;
`

// The strong rule serves PatEntryStep's inline emphasis.
export const SidePanelBody = styled.p`
  font-size: 13px;
  font-weight: 400;
  color: #4a4a68;
  margin: 0;

  strong {
    font-weight: 700;
  }
`
